import { useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'

export const ECOSYSTEM_CAPABILITY_CONTRACTS = [
  {
    id: 'performance-governance',
    origins: [
      'games/portal-battle-arena/src/game/systems/DynamicQualityScaler.ts',
      'games/portal-battle-arena/src/game/systems/FrameBudgetScheduler.ts',
      'artifacts/elyon/src/systems/performance-engine.ts',
    ],
    target: 'AdaptivePerformanceGovernor',
    signal: 'scene.userData.inyronPerformance',
  },
  {
    id: 'autonomy-integrity',
    origins: [
      'games/portal-battle-arena/src/game/systems/AIUpdateScheduler.ts',
      'games/portal-battle-arena/src/game/systems/EncounterDirector.ts',
      'artifacts/elyon/src/lib/world-004-ai.ts',
      'artifacts/elyon/src/lib/boss-ai-engine.ts',
    ],
    target: 'AutonomyEngines',
    signal: 'scene.userData.inyronStateSentinel + inyronSceneSentinel + inyronRigSentinel',
  },
  {
    id: 'cinematic-impact-camera',
    origins: [
      'games/portal-battle-arena/src/game/systems/CameraShakeController.ts',
      'artifacts/elyon/src/lib/camera-shake-envelope.ts',
      'artifacts/elyon/src/lib/intelligent-camera-engine.ts',
    ],
    target: 'CinematicCombatDirector',
    signal: 'scene.userData.inyronCinematicCamera',
  },
  {
    id: 'impact-audio',
    origins: [
      'games/portal-battle-arena/src/game/systems/AudioSystem.ts',
      'games/portal-battle-arena/src/game/systems/AudioMixing.ts',
      'artifacts/elyon/src/systems/CombatAudioEventBridge.ts',
    ],
    target: 'ImpactAudio',
    signal: 'scene.userData.inyronImpactAudio',
  },
  {
    id: 'impact-vfx',
    origins: [
      'games/portal-battle-arena/src/game/systems/ImpactParticleSystem.ts',
      'games/portal-battle-arena/src/game/systems/ParticleEffectSystem.ts',
      'artifacts/elyon/src/lib/vfx-particle-system.ts',
      'artifacts/elyon/src/lib/enemy-impact-feedback.ts',
    ],
    target: 'ImpactFeedback',
    signal: 'scene.userData.inyronImpactVfx',
  },
  {
    id: 'combat-physics-reality',
    origins: [
      'games/portal-battle-arena/src/game/systems/CombatSystem.ts',
      'games/portal-battle-arena/src/game/systems/KnockbackSystem.ts',
      'artifacts/elyon/src/systems/AdvancedCombatSystem.ts',
      'artifacts/elyon/src/lib/world-physics.ts',
    ],
    target: 'CombatRealityEngine + simulation',
    signal: 'scene.userData.inyronCombatReality',
  },
  {
    id: 'living-universe',
    origins: [
      'lib/living-universe/',
      'artifacts/elyon/src/lib/living-universe.ts',
      'games/portal-battle-arena/src/game/systems/livingUniverse.ts',
    ],
    target: 'AutonomyEngines + livingUniverse',
    signal: 'scene.userData.inyronLife',
  },
  {
    id: 'world-construction',
    origins: [
      'games/portal-battle-arena/src/game/systems/EnvironmentalWorldBuilder.ts',
      'games/portal-battle-arena/src/game/systems/ScenarioArchitectureOmega3D.tsx',
      'artifacts/elyon/src/systems/fase8-domain-os-config.ts',
    ],
    target: 'WorldConstructionLogicLayer + FightScenarioArchitectureRuntime',
    signal: 'scene.userData.inyronConstructionLogic + inyronScenarioArchitectureBound',
  },
  {
    id: 'hero-combat-bridge',
    origins: [
      'artifacts/elyon/src/lib/combat-engine.ts',
      'artifacts/elyon/src/lib/player-combat-animation-bridge.ts',
      'games/portal-battle-arena/src/game/systems/UltimateAbilitySystem.ts',
    ],
    target: 'HeroCombatRuntimeBridge',
    signal: 'window.__INYRON_HERO_COMBAT_BRIDGE__',
  },
  {
    id: 'supreme-engine-stack',
    origins: [
      'games/portal-battle-arena/src/game/systems/PerformanceIntegration.ts',
      'artifacts/elyon/src/lib/core-002-performance.ts',
    ],
    target: 'SupremeGameEngines + MetamotorRuntimeBridge',
    signal: 'scene.userData.inyronEngineHealth + inyronMetamotorHealth',
  },
] as const

export type EcosystemCapabilityId = typeof ECOSYSTEM_CAPABILITY_CONTRACTS[number]['id']

export type EcosystemCapabilityReport = {
  green: boolean
  connected: Record<EcosystemCapabilityId, boolean>
  connectedCount: number
  requiredCount: number
  missing: EcosystemCapabilityId[]
  origins: typeof ECOSYSTEM_CAPABILITY_CONTRACTS
  observedAt: number
}

export function EcosystemCapabilityBridge() {
  const { scene } = useThree()
  const timer = useRef(0)

  useFrame((_, dt) => {
    timer.current += Math.max(0, dt)
    if (timer.current < .5) return
    timer.current = 0

    const runtimeWindow = window as typeof window & {
      __INYRON_HERO_COMBAT_BRIDGE__?: { active?: boolean }
      __INYRON_ECOSYSTEM_CAPABILITY_BRIDGE__?: EcosystemCapabilityReport
    }

    const connected: Record<EcosystemCapabilityId, boolean> = {
      'performance-governance': Boolean(scene.userData.inyronPerformance),
      'autonomy-integrity': Boolean(
        scene.userData.inyronStateSentinel?.ok
        && scene.userData.inyronSceneSentinel?.ok
        && scene.userData.inyronRigSentinel?.ok,
      ),
      'cinematic-impact-camera': Boolean(scene.userData.inyronCinematicCamera?.active),
      'impact-audio': Boolean(scene.userData.inyronImpactAudio?.connected),
      'impact-vfx': Boolean(scene.userData.inyronImpactVfx?.active),
      'combat-physics-reality': Boolean(scene.userData.inyronCombatReality?.active),
      'living-universe': Boolean(scene.userData.inyronLife),
      'world-construction': Boolean(
        scene.userData.inyronConstructionLogic
        && scene.userData.inyronScenarioArchitectureBound,
      ),
      'hero-combat-bridge': Boolean(runtimeWindow.__INYRON_HERO_COMBAT_BRIDGE__?.active),
      'supreme-engine-stack': Boolean(
        scene.userData.inyronEngineHealth?.engineCount >= 10
        && scene.userData.inyronMetamotorHealth?.prometheus?.accepted,
      ),
    }

    const missing = ECOSYSTEM_CAPABILITY_CONTRACTS
      .map(contract => contract.id)
      .filter(id => !connected[id])
    const report: EcosystemCapabilityReport = {
      green: missing.length === 0,
      connected,
      connectedCount: ECOSYSTEM_CAPABILITY_CONTRACTS.length - missing.length,
      requiredCount: ECOSYSTEM_CAPABILITY_CONTRACTS.length,
      missing,
      origins: ECOSYSTEM_CAPABILITY_CONTRACTS,
      observedAt: performance.now(),
    }

    scene.userData.inyronEcosystemCapabilityBridge = report
    runtimeWindow.__INYRON_ECOSYSTEM_CAPABILITY_BRIDGE__ = report
  })

  return null
}
